import { ORDER_STATUS } from "@/helpers/order/status";

export const TYPE_KITCHENHUB_ORDER = 4;

// order/models.py KITCHENHUB_TO_BIYO_ORDER_STATUS, mirrored client-side.
const KITCHENHUB_TO_BIYO_STATUS = {
  new: ORDER_STATUS.PENDING,
  accepted: ORDER_STATUS.OPEN,
  completed: ORDER_STATUS.COMPLETED,
  cancelled: ORDER_STATUS.CANCELLED,
};

// panel/views.py's KITCHENHUB_PROVIDER_PAYMENT_TYPES, mirrored client-side —
// keyed by KitchenHub's stable provider.id slug (not the display name, which
// is free text and shouldn't be trusted to always match a real PaymentType).
// Single source of truth for both resolvers below.
const KITCHENHUB_PROVIDER_PAYMENT_TYPES = {
  ubereats: { id: -1, name: "UberEats" },
  doordash: { id: -2, name: "DoorDash" },
};

function resolveKitchenHubPaymentType(order) {
  return KITCHENHUB_PROVIDER_PAYMENT_TYPES[order.kitchenhub_provider_id] || null;
}

// Resolves the exact PaymentType.name to use as Payment.payment_type for a
// paid KitchenHub order (see completeKitchenHubOrder in OnlineOrders.vue).
// Prefers the stable provider id map; falls back to the free-text provider
// name for a provider we don't have a seeded PaymentType for yet.
export function resolveKitchenHubPaymentTypeName(order) {
  const paymentType = resolveKitchenHubPaymentType(order);
  return (paymentType && paymentType.name) || order.provider_name || "KitchenHub";
}

// Resolves the seeded PaymentType's id (-1 UberEats, -2 DoorDash) — recorded
// in Payment.processor_response since Payment.payment_type is a free-text
// name with no FK back to PaymentType, so this is the only place on the
// Payment row that ties back to the exact PaymentType record by id. null for
// a provider we don't have a seeded PaymentType for.
export function resolveKitchenHubPaymentTypeId(order) {
  const paymentType = resolveKitchenHubPaymentType(order);
  return paymentType ? paymentType.id : null;
}

function toNumber(value, fallback = 0) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

// Real orders (placed against a menu we pushed) carry partner_id == our
// product id, since build_menu_payload() sets partner_id = str(product.id).
// Sandbox/mock orders always have partner_id: null, so fall back to a
// best-effort name match rather than silently dropping the item.
function findMatchingProduct(item, products) {
  if (item.partner_id != null) {
    const partnerId = String(item.partner_id);
    const byPartnerId = products.find((p) => String(p.id) === partnerId);
    if (byPartnerId) return byPartnerId;
  }
  const name = (item.name || "").trim().toLowerCase();
  if (!name) return null;
  return products.find((p) => (p.name || "").trim().toLowerCase() === name) || null;
}

// KitchenHub gives a single "name" field; Customer.first_name is a required,
// non-nullable model field, so this must never end up empty.
function splitName(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return { firstName: "KitchenHub Customer", lastName: null };
  const [firstName, ...rest] = trimmed.split(" ");
  return { firstName, lastName: rest.join(" ") || null };
}

// Sent as a normal order.customer object — the same generic find-or-create-by
// phone/email path (OrderCustomSyncView -> CustomerSyncSerializer) every other
// order type already goes through, not a KitchenHub-specific mechanism.
// `addresses` is required by that serializer even when empty.
function buildCustomer(khCustomer) {
  if (!khCustomer || (!khCustomer.name && !khCustomer.phone_number && !khCustomer.email)) return null;
  const { firstName, lastName } = splitName(khCustomer.name);
  return {
    first_name: firstName,
    last_name: lastName,
    phone: khCustomer.phone_number || null,
    email: khCustomer.email || null,
    addresses: [],
  };
}

// Real orders (placed against a menu we pushed) carry option.partner_id ==
// our "mod-<Modifier.id>" partner id, since build_menu_payload() sets that
// convention on every pushed options[] entry. Sandbox/mock orders always have
// partner_id: null (same as items[].partner_id), so fall back to a
// case-insensitive name match against the matched product's own modifier
// groups. Returns null (never a partial match) when nothing resolves —
// OrderModifier.group/oryginal are required FKs with no fallback value, so an
// unresolved option must never become a modifier object; see describeOptions.
function findMatchingModifier(khOption, product) {
  if (!product || !Array.isArray(product.modifier_groups)) return null;

  if (khOption.partner_id != null) {
    const match = /^mod-(\d+)$/.exec(String(khOption.partner_id));
    if (match) {
      const modifierId = Number(match[1]);
      for (const group of product.modifier_groups) {
        const modifier = (group.modifiers || []).find((m) => m.id === modifierId);
        if (modifier) return { modifier, group };
      }
    }
  }

  const name = (khOption.name || "").trim().toLowerCase();
  if (!name) return null;
  for (const group of product.modifier_groups) {
    const modifier = (group.modifiers || []).find((m) => (m.name || "").trim().toLowerCase() === name);
    if (modifier) return { modifier, group };
  }
  return null;
}

// Shape matches OrderModifiersSyncSerializer exactly (api/serializers.py):
// name, price, price_original, cost, group, oryginal, void_status.
function buildOrderModifier(khOption, modifier, group) {
  const price = toNumber(khOption.price, modifier.price);
  return {
    name: modifier.name,
    price,
    price_original: khOption.original_price != null ? toNumber(khOption.original_price, price) : null,
    cost: modifier.cost || 0,
    group: group.id,
    oryginal: modifier.id,
    void_status: false,
  };
}

// Unmatched options (no resolvable Modifier — see findMatchingModifier) still
// get folded into the item note as readable text, same as before this
// mapping existed, rather than silently dropped.
function describeOptions(options) {
  if (!Array.isArray(options) || !options.length) return "";
  return options
    .map((opt) => {
      const price = toNumber(opt.price, 0);
      return price > 0 ? `${opt.name} (+${price.toFixed(2)})` : opt.name;
    })
    .join(", ");
}

function buildOrderItem(khItem, products, terminalId, index) {
  const matched = findMatchingProduct(khItem, products);
  const quantity = toNumber(khItem.quantity, 1) || 1;
  const price = toNumber(khItem.price, matched ? matched.price : 0);

  const khOptions = Array.isArray(khItem.options) ? khItem.options : [];
  const modifiers = [];
  const unmatchedOptions = [];
  khOptions.forEach((khOption) => {
    const resolved = findMatchingModifier(khOption, matched);
    if (resolved) {
      modifiers.push(buildOrderModifier(khOption, resolved.modifier, resolved.group));
    } else {
      unmatchedOptions.push(khOption);
    }
  });
  const optionsText = describeOptions(unmatchedOptions);

  const noteParts = [];
  if (!matched) {
    noteParts.push(`[KitchenHub: "${khItem.name}" not matched to a product — verify manually]`);
  }
  if (khItem.instructions) noteParts.push(khItem.instructions);
  if (optionsText) noteParts.push(optionsText);

  return {
    // Same terminal_id + index + timestamp convention as orderMixin.js's
    // getItemIterator() for every other order type.
    local_id: `${terminalId || "000"}_${index}_${Date.now()}`,
    product: matched ? matched.id : null,
    name: matched ? matched.name : khItem.name,
    quantity,
    price,
    // OrderItem.cost/tax are non-nullable model fields with no default — the
    // POS cart always sends them (see orderDomain.js's createOrderItem), so
    // this must too or the sync endpoint 400s with "This field is required."
    // KitchenHub never returns either of these, so always default to 0.00
    // rather than deriving from the matched product (which may not exist
    // or be stale relative to what was actually charged).
    cost: 0.00,
    tax: 0.00,
    tax_items: [],
    // We don't get a per-item tax breakdown from KitchenHub, only the order-level
    // charges.tax total (applied directly to order.tax_total below) — mark items
    // untaxed rather than implying a per-item split we don't actually have.
    tax_status: 1,
    discount: 0,
    notes: noteParts.join(" — ") || null,
    modifiers,
    void_status: null,
    printers: matched && matched.printers ? matched.printers : [],
    has_kitchen_updates: !!(matched && matched.printers && matched.printers.length),
    sent_to_kitchen: false,
    // Modifier prices are informational only here (kitchen ticket / item
    // breakdown) — never summed into subtotal/grand_total. Nothing downstream
    // recomputes an order's charge from items+modifiers for a KitchenHub
    // order: paid orders complete via order.grand_total directly
    // (completeKitchenHubOrder), and the Pay/checkout screen's amount due
    // (getOrderFinalTotal) also reads order.grand_total directly, never
    // items/modifiers — so there is no double-counting risk in pricing
    // modifiers accurately here.
    subtotal: price * quantity,
    grand_total: price * quantity,
  };
}

/**
 * Build a local Biyo order object from a KitchenHub order detail response
 * (the flat shape returned by GET /api/kitchenhub/orders/<id>/:
 * { order: { order, provider, store, customer, items, delivery, payment,
 * charges, timestamps, location }, brand_id, store_id }).
 *
 * KitchenHub is treated as the source of truth for money already charged —
 * subtotal/tax/discount/total are taken directly from `charges` rather than
 * recomputed client-side, the same way online orders arrive pre-priced.
 *
 * `local_id` follows the standard `${terminalId}_${...}_${Date.now()}`
 * convention used everywhere else in the app (see orders.js's initNewOrder /
 * orderMixin.js's getItemIterator) rather than a KitchenHub-specific scheme,
 * so it carries no KitchenHub identity of its own — `kitchenhub_order_id` is
 * sent as its own explicit field instead, both for the receive-side
 * idempotency check (orders/receiveKitchenHubOrder) and for the backend to
 * register the KitchenHubOrder routing mapping.
 */
export function buildOrderFromKitchenHub(detailResponse, products = [], terminalId = null) {
  const khOrder = detailResponse.order || {};
  const orderInfo = khOrder.order || {};
  const charges = khOrder.charges || {};
  const customer = khOrder.customer || {};
  const provider = khOrder.provider || {};
  const items = Array.isArray(khOrder.items) ? khOrder.items : [];

  const status = KITCHENHUB_TO_BIYO_STATUS[orderInfo.status] ?? ORDER_STATUS.PENDING;
  const providerName = provider.name || "KitchenHub";
  const description = customer.name
    ? `${providerName} order for ${customer.name}${customer.phone_number ? ` (${customer.phone_number})` : ""}`
    : `${providerName} order via KitchenHub`;

  return {
    local_id: `${terminalId || "000"}_${Date.now()}`,
    kitchenhub_order_id: String(orderInfo.id),
    kitchenhub_brand_id: detailResponse.brand_id,
    customer: buildCustomer(customer),
    // Display-only — not an Order model field, so the sync endpoint just
    // ignores it. Lets the UI show "UberEats" etc. instead of the generic
    // "KitchenHub" aggregator name.
    provider_name: providerName,
    // Also display-only — KitchenHub's stable provider slug (e.g. "ubereats"),
    // used by resolveKitchenHubPaymentTypeName to pick the right PaymentType.
    kitchenhub_provider_id: provider.id || null,
    // Also display-only. order.order.paid tells us the customer already paid
    // KitchenHub/the delivery platform directly — the POS has no payment left
    // to collect, so OnlineOrders.vue shows "Complete" instead of "Pay" for
    // these and builds the Payment record from `charges` at that point
    // (see completeKitchenHubOrder) rather than routing through the Pay screen.
    is_kitchenhub_paid: !!orderInfo.paid,
    type: TYPE_KITCHENHUB_ORDER,
    status,
    related_store: detailResponse.store_id,
    terminal_id: -2,
    description,
    open_date: new Date().toISOString(),
    subtotal: toNumber(charges.subtotal),
    tax_total: toNumber(charges.tax),
    discount_orders: toNumber(charges.discount),
    discount_total: 0,
    tips_total: toNumber(charges.tips),
    grand_total: toNumber(charges.total),
    items: items.map((item, index) => buildOrderItem(item, products, terminalId, index)),
    payments: [],
  };
}
